import { createContext, useContext, type ReactNode } from 'react'
import {
  EQUIPMENT_PURPOSES,
  INVENTORY_TASK_MATERIAL_TYPES,
  ROOM_PURPOSES,
  SCHEDULE_SCOPE_NOTE,
  STORAGE_PURPOSES,
  type EquipmentPurpose,
} from '@/types/equipment'

/**
 * Which page the equipment screens are on (Cleaning module, z8nrz7ewt1).
 *
 * A room is equipment with purpose ROOM, so the Cleaning page IS the Equipment
 * page — same grids, modals, tasks and calendar — showing only rooms and
 * calling them rooms. Each page lists its own share and nothing else: the
 * Equipment page every purpose but Room, the Cleaning page Room only, where Add
 * Room / Add Room Type always save purpose Room. Groups follow the page too: a
 * group made on the Cleaning page is a Room Group. Every equipment screen reads
 * its purposes and its words from here rather than taking them as props, so a component three levels down
 * (a modal inside a tab) cannot fall back to the other page's wording.
 *
 * Same idea as the Procedures page's `variant` (Kits, MES, CAPA): one module,
 * one `copy` object per variant, so a new variant cannot inherit another's text.
 *
 * `storage` is Inventory's Tasks tab (z8nrz7fjzc): storage units', materials'
 * and purchase orders' tasks, made by inventory actions rather than by hand.
 * It is not a page of its own — storage units are still set up on the
 * Equipment page, which keeps listing them.
 */

export type EquipmentVariant = 'equipment' | 'room' | 'storage'

export interface EquipmentCopy {
  /** Page heading: "Equipment" / "Cleaning". */
  page: string
  /** A unit, as a heading or button noun: "Equipment" / "Room". */
  unit: string
  /** A unit inside a sentence: "equipment" / "room". */
  unitLower: string
  /** Units inside a sentence: "equipment" / "rooms". */
  unitsLower: string
  unitId: string
  type: string
  group: string
  addUnit: string
  editUnit: string
  addType: string
  editType: string
  search: string
  loading: string
  /** The Tasks tab's subtitle. */
  worklist: string
  /** The Tasks tab's search box. */
  taskSearch: string
  /** The occurrence calendar when a period has nothing in it. */
  noEvents: string
  /** The type-level "is it on a cycle" filter: all / on a cycle / not on one. */
  maintainedAll: string
  maintained: string
  unmaintained: string
  /** A filtered grid with nothing left in it. */
  noMatch: string
  /** The type grid: its unit-count column, empty state and edit button. */
  unitCount: string
  noTypeMatch: string
  /** The "serviced on a cycle" switch on a type. */
  maintainedHelp: string
  /** What a type's purpose means, under the Purpose select. */
  purposeHelp: string
  /** The Add dialog's subtitle, for a type on a cycle and one that is not. */
  registerScheduled: string
  registerUnscheduled: string
  /** What a calendar event is OF, before "event(s)": "equipment" / "cleaning". */
  eventNoun: string
  /** Counting units of a type: "1 unit", "3 units" / "1 room", "3 rooms". */
  unitOne: string
  unitMany: string
  /** Under each grid: where a schedule is defined and what it applies to. */
  scheduleNote: string
  /** A type name already used on the OTHER page — names are unique across both. */
  takenElsewhere: string
  /** Example names in the add dialogs. */
  typeExample: string
  unitExample: string
  /** Hints, labels and placeholders that would otherwise talk about machines and maintenance. */
  notOnCycle: string
  cycleLabel: string
  checklistHint: string
  newEventHint: string
  assigneeHint: string
  startPlaceholder: string
  endPlaceholder: string
  typeOnlyHint: string
  tagHint: string
}

export interface EquipmentScope {
  variant: EquipmentVariant
  /** The purposes this page lists — every query on it asks for these and no others. */
  purposes: readonly EquipmentPurpose[]
  /** Tasks on materials of these types too (Inventory, z8nrz7fjzc). Absent: units only. */
  materialTypes?: readonly string[]
  /** Purchase orders' tasks too (Inventory, z8nrz7fjzc). */
  orders?: boolean
  /**
   * Its tasks are made by inventory actions — a lot received, an order sent, a
   * transfer ready — never by hand (Inventory, z8nrz7fjzc): it lists only
   * those (no unit's maintenance), with no New Event, event-type legend or PM / CM.
   */
  tasksFromActions?: boolean
  /** Whether a unit's purpose can be chosen. A room is always a room. */
  choosePurpose: boolean
  /** Whether the Storage tab exists — rooms never hold stock. */
  showStorage: boolean
  /** Prefix for per-user table layouts, so the two pages keep separate column choices. */
  tablePrefix: string
  copy: EquipmentCopy
}

export const EQUIPMENT_SCOPE: EquipmentScope = {
  variant: 'equipment',
  purposes: EQUIPMENT_PURPOSES,
  choosePurpose: true,
  showStorage: true,
  tablePrefix: 'equipment',
  copy: {
    page: 'Equipment',
    unit: 'Equipment',
    unitLower: 'equipment',
    unitsLower: 'equipment',
    unitId: 'Equipment ID',
    type: 'Equipment Type',
    group: 'Equipment Group',
    addUnit: 'Add Equipment',
    editUnit: 'Edit Equipment',
    addType: 'Add Equipment Type',
    editType: 'Edit Equipment Type',
    search: 'Search equipment, type, group…',
    loading: 'Loading equipment…',
    worklist: 'Maintenance worklist',
    taskSearch: 'Search equipment, assignee, SOP…',
    noEvents: 'No equipment events in this period.',
    maintainedAll: 'All maintenance',
    maintained: 'Maintained',
    unmaintained: 'Not maintained',
    noMatch: 'No equipment matches your search or filter.',
    unitCount: 'Units',
    noTypeMatch: 'No equipment types match your search or filter.',
    maintainedHelp: 'Serviced on a cycle. Switch off for shelving, benches and racks.',
    purposeHelp:
      'Whether this equipment acts on material or holds it. Separate from the group, which names the manufacturing stage.',
    registerScheduled: 'Register a new equipment unit. A new unit inherits its type’s schedule.',
    registerUnscheduled: 'Register a new equipment unit. This type is not maintained, so the unit gets no schedule.',
    eventNoun: 'equipment',
    unitOne: 'unit',
    unitMany: 'units',
    scheduleNote: SCHEDULE_SCOPE_NOTE,
    takenElsewhere: 'That name is used by a room type on the Cleaning page.',
    typeExample: 'e.g. Cell washing systems',
    unitExample: 'e.g. APH-A4',
    notOnCycle: 'Not maintained — no service cycle',
    cycleLabel: 'Maintenance',
    checklistHint: 'Shown to technicians before starting a maintenance event on this type.',
    newEventHint: 'Create a Corrective (CM) or Preventive (PM) maintenance event',
    assigneeHint: 'Preventive maintenance generated for this type is assigned to them automatically. Left unset, generated tasks appear in the Unassigned lane instead.',
    startPlaceholder: 'Starting scheduled maintenance…',
    endPlaceholder: 'Outcome of the maintenance…',
    typeOnlyHint: 'Defines the type only. Units of it are added separately, when the machines actually arrive — its schedule is shared by all of them.',
    tagHint: "Encodes the unit's permanent id, so the tag stays valid if this machine is renamed or moved.",
  },
}

export const ROOM_SCOPE: EquipmentScope = {
  variant: 'room',
  purposes: ROOM_PURPOSES,
  choosePurpose: false,
  showStorage: false,
  tablePrefix: 'room',
  copy: {
    page: 'Cleaning',
    unit: 'Room',
    unitLower: 'room',
    unitsLower: 'rooms',
    unitId: 'Room ID',
    type: 'Room Type',
    group: 'Room Group',
    addUnit: 'Add Room',
    editUnit: 'Edit Room',
    addType: 'Add Room Type',
    editType: 'Edit Room Type',
    search: 'Search rooms, type, group…',
    loading: 'Loading rooms…',
    worklist: 'Cleaning worklist',
    taskSearch: 'Search rooms, assignee, SOP…',
    noEvents: 'No cleaning events in this period.',
    maintainedAll: 'All cleaning',
    maintained: 'Cleaned on a cycle',
    unmaintained: 'Not cleaned on a cycle',
    noMatch: 'No rooms match your search or filter.',
    unitCount: 'Rooms',
    noTypeMatch: 'No room types match your search or filter.',
    maintainedHelp: 'Cleaned on a cycle. Switch off for a room with no cleaning schedule.',
    purposeHelp: 'A room is always a room.',
    registerScheduled: 'Register a new room. A new room inherits its type’s cleaning schedule.',
    registerUnscheduled: 'Register a new room. This type is not cleaned on a cycle, so the room gets no schedule.',
    eventNoun: 'cleaning',
    unitOne: 'room',
    unitMany: 'rooms',
    scheduleNote: 'schedules are defined per Room Type and apply to all rooms of that type.',
    takenElsewhere: 'That name is used by an equipment type on the Equipment page.',
    typeExample: 'e.g. Cleanroom Grade B',
    unitExample: 'e.g. CR-B-101',
    notOnCycle: 'Not cleaned on a cycle — no cleaning schedule',
    cycleLabel: 'Cleaning',
    checklistHint: 'Shown to the cleaning crew before starting a cleaning on this type.',
    newEventHint: 'Create a corrective (CM) or scheduled (PM) cleaning for a room',
    assigneeHint: 'Scheduled cleaning generated for this type is assigned to them automatically. Left unset, generated tasks appear in the Unassigned lane instead.',
    startPlaceholder: 'Starting scheduled cleaning…',
    endPlaceholder: 'Outcome of the cleaning…',
    typeOnlyHint: 'Defines the type only. Rooms of it are added separately — its cleaning schedule is shared by all of them.',
    tagHint: "Encodes the room's permanent id, so the tag stays valid if the room is renamed.",
  },
}

export const STORAGE_SCOPE: EquipmentScope = {
  variant: 'storage',
  purposes: STORAGE_PURPOSES,
  materialTypes: INVENTORY_TASK_MATERIAL_TYPES,
  orders: true,
  tasksFromActions: true,
  choosePurpose: false,
  showStorage: true,
  tablePrefix: 'storage',
  copy: {
    page: 'Inventory',
    unit: 'Storage unit',
    unitLower: 'storage unit',
    unitsLower: 'storage units',
    // A row is a storage unit, a material or a purchase order (z8nrz7fjzc).
    unitId: 'Item',
    type: 'Equipment Type',
    group: 'Equipment Group',
    addUnit: 'Add Storage Unit',
    editUnit: 'Edit Storage Unit',
    addType: 'Add Equipment Type',
    editType: 'Edit Equipment Type',
    search: 'Search storage units, type, group…',
    loading: 'Loading storage units…',
    worklist: 'Inventory worklist',
    taskSearch: 'Search items, assignee, SOP…',
    noEvents: 'No storage events in this period.',
    maintainedAll: 'All maintenance',
    maintained: 'Maintained',
    unmaintained: 'Not maintained',
    noMatch: 'No storage units match your search or filter.',
    unitCount: 'Units',
    noTypeMatch: 'No equipment types match your search or filter.',
    maintainedHelp: 'Serviced on a cycle. Switch off for shelving, benches and racks.',
    purposeHelp: 'A storage unit holds material.',
    registerScheduled: 'Register a new storage unit. A new unit inherits its type’s schedule.',
    registerUnscheduled: 'Register a new storage unit. This type is not maintained, so the unit gets no schedule.',
    eventNoun: 'storage',
    unitOne: 'unit',
    unitMany: 'units',
    scheduleNote: SCHEDULE_SCOPE_NOTE,
    takenElsewhere: 'That name is used by a room type on the Cleaning page.',
    typeExample: 'e.g. Ultra-low Freezer',
    unitExample: 'e.g. ULF-01',
    notOnCycle: 'Not maintained — no service cycle',
    cycleLabel: 'Maintenance',
    checklistHint: 'Shown to technicians before starting a maintenance event on this type.',
    newEventHint: 'Create a Corrective (CM) or Preventive (PM) event for a storage unit',
    assigneeHint: 'Preventive maintenance generated for this type is assigned to them automatically. Left unset, generated tasks appear in the Unassigned lane instead.',
    startPlaceholder: 'Starting scheduled maintenance…',
    endPlaceholder: 'Outcome of the maintenance…',
    typeOnlyHint: 'Defines the type only. Units of it are added separately — its schedule is shared by all of them.',
    tagHint: "Encodes the unit's permanent id, so the tag stays valid if this unit is renamed or moved.",
  },
}

const ScopeContext = createContext<EquipmentScope>(EQUIPMENT_SCOPE)

export function EquipmentScopeProvider({ scope, children }: { scope: EquipmentScope; children: ReactNode }) {
  return <ScopeContext.Provider value={scope}>{children}</ScopeContext.Provider>
}

/** The page the equipment screens are on; the Equipment page when nothing says otherwise. */
export function useEquipmentScope(): EquipmentScope {
  return useContext(ScopeContext)
}
